import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft, Calendar, MapPin, Users, Share2, DollarSign, Ticket,
  Edit3, Trash2, Image as ImageIcon, Loader2, Check, UserPlus, Clock,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import EventPhotoDisplay from '@/components/EventPhotoDisplay';
import EventPhotoUploader from '@/components/EventPhotoUploader';
import EventPhotoWall from '@/components/EventPhotoWall';
import WorkshopCheckoutUpgradeCard from '@/components/WorkshopCheckoutUpgradeCard';
import WorkshopMemberDiscountBanner from '@/components/WorkshopMemberDiscountBanner';
import { QRCodeCanvas } from 'qrcode.react';
import api from '@/lib/api';

const formatEventDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function EventDetailPage() {
  const { eventId } = useParams();
  const { user, isAuthenticated, initialized: authInitialized } = useAuth();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvping, setRsvping] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);

  // Annual-subscriber guest pass (Phase 3)
  const [guestPass, setGuestPass] = useState(null);
  const [bringGuest, setBringGuest] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [invitingGuest, setInvitingGuest] = useState(false);
  const [myTicket, setMyTicket] = useState(null);

  // Monotonic request counter — the latest issued request wins. Prevents a
  // stale /public response from overwriting a newer authenticated response
  // when AuthContext hydrates after mount (fixes Phase-2 CTA race).
  const reqCounter = useRef(0);

  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/events/${eventId}` : '';

  const loadEvent = useCallback(async () => {
    const myReq = ++reqCounter.current;
    setLoading(true);
    try {
      const endpoint = isAuthenticated
        ? `/events/${eventId}`
        : `/events/${eventId}/public`;
      const res = await api.get(endpoint);
      if (myReq !== reqCounter.current) return;   // stale response — discard
      setEvent(res.data);
    } catch (e) {
      if (myReq !== reqCounter.current) return;   // stale — discard
      if (e.response?.status === 404) {
        toast.error('Event not found');
        navigate(isAuthenticated ? '/my-city' : '/');
        return;
      }
      // Auth fallthrough: try public preview
      if (isAuthenticated && e.response?.status === 401) {
        try {
          const r2 = await api.get(`/events/${eventId}/public`);
          if (myReq !== reqCounter.current) return;
          setEvent(r2.data);
        } catch {
          toast.error('Could not load event');
        }
      } else {
        toast.error(e.response?.data?.detail || 'Could not load event');
      }
    }
    if (myReq === reqCounter.current) setLoading(false);
  }, [eventId, isAuthenticated, navigate]);

  // Wait for AuthContext to hydrate before firing. Otherwise the first render
  // sees isAuthenticated=false, hits /public (no workshop annotation), and
  // that response can race the authenticated one and overwrite it.
  useEffect(() => {
    if (!authInitialized) return;
    loadEvent();
  }, [loadEvent, authInitialized]);

  // Load guest pass state for authenticated members viewing a workshop
  useEffect(() => {
    if (!isAuthenticated || !event || (event.event_type || '').toLowerCase() !== 'workshop') return;
    let alive = true;
    api.get('/guest-pass/me')
      .then((res) => { if (alive) setGuestPass(res.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [isAuthenticated, event]);

  // Load the member's check-in ticket code once registered
  useEffect(() => {
    if (!isAuthenticated || !event) return;
    const registered = (event.user_rsvped && event.user_rsvp_status === 'confirmed') || event.user_has_paid;
    if (!registered) { setMyTicket(null); return; }
    let alive = true;
    api.get(`/events/${eventId}/my-ticket`)
      .then((res) => { if (alive) setMyTicket(res.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [isAuthenticated, event, eventId]);

  // Open Graph / Twitter meta tags for rich link previews when the event URL is shared
  useEffect(() => {
    if (!event) return;
    const title = `${event.title} · CaliMoms Connect`;
    const description = event.description
      ? event.description.slice(0, 200)
      : `Join us on ${new Date(event.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} in ${event.city || 'California'}. RSVP on CaliMoms Connect.`;
    const firstPhoto = (event.photos && event.photos[0]) || event.photo_url;
    const imageUrl = firstPhoto
      ? (firstPhoto.startsWith('http') ? firstPhoto : `${process.env.REACT_APP_BACKEND_URL}${firstPhoto}`)
      : '';
    const canonical = typeof window !== 'undefined' ? `${window.location.origin}/events/${eventId}` : '';
    const prevTitle = document.title;
    document.title = title;

    const tags = [
      ['og:title', title],
      ['og:description', description],
      ['og:type', 'website'],
      ['og:url', canonical],
      ...(imageUrl ? [['og:image', imageUrl]] : []),
      ['twitter:card', imageUrl ? 'summary_large_image' : 'summary'],
      ['twitter:title', title],
      ['twitter:description', description],
      ...(imageUrl ? [['twitter:image', imageUrl]] : []),
      ['description', description],
    ];
    const created = [];
    tags.forEach(([key, value]) => {
      const isOg = key.startsWith('og:');
      const selector = isOg ? `meta[property="${key}"]` : `meta[name="${key}"]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        if (isOg) el.setAttribute('property', key);
        else el.setAttribute('name', key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', value);
    });

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
    };
  }, [event, eventId]);

  const canManagePhotos = !!user && (user.is_admin || (user.can_manage_events && event?.created_by === user.id));
  const isOwner = canManagePhotos;

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: event?.title || 'CaliMoms Event',
          text: `Join me at ${event?.title || 'this CaliMoms event'}!`,
          url: shareUrl,
        });
        return;
      }
    } catch { /* user cancelled share; fall through to copy */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedShare(true);
      toast.success('Share link copied — paste it anywhere!');
      setTimeout(() => setCopiedShare(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleRSVP = async () => {
    if (!isAuthenticated) {
      navigate(`/signup?redirect=/events/${eventId}`);
      return;
    }
    setRsvping(true);
    try {
      const res = await api.post(`/events/${eventId}/rsvp`);
      toast.success(res.data.rsvped ? `You're ${res.data.status === 'waitlisted' ? 'on the waitlist' : 'confirmed'}!` : 'RSVP cancelled');
      await loadEvent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'RSVP failed');
    }
    setRsvping(false);
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const isWorkshop = (event?.event_type || '').toLowerCase() === 'workshop';
      if (isWorkshop) {
        const body = {};
        if (bringGuest && guestName.trim() && guestEmail.trim()) {
          body.bring_guest = true;
          body.guest_first_name = guestName.trim();
          body.guest_email = guestEmail.trim();
        }
        const res = await api.post(`/workshops/${eventId}/register`, body);
        if (res.data?.url) {
          window.location.href = res.data.url;
          return;
        }
        // credit-applied or waitlisted — no redirect
        toast.success(res.data?.message || 'You\'re in!');
        if (res.data?.guest) {
          if (res.data.guest.status === 'invited') {
            toast.success(res.data.guest.message);
          } else if (res.data.guest.message) {
            toast.error(`Guest invite: ${res.data.guest.message}`);
          }
          try { const gp = await api.get('/guest-pass/me'); setGuestPass(gp.data); } catch { /* */ }
        }
        await loadEvent();
      } else {
        const res = await api.post(`/events/${eventId}/checkout`, { origin_url: window.location.origin });
        if (res.data?.url) {
          window.location.href = res.data.url;
        } else {
          toast.error('Checkout not available');
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
    }
    setCheckoutLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${event.title}"? This removes all RSVPs and cannot be undone.`)) return;
    try {
      await api.delete(`/events/${eventId}`);
      toast.success('Event deleted');
      navigate(user?.is_admin ? '/admin/dashboard' : '/coadmin/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handlePhotosChange = async (nextPhotos) => {
    try {
      await api.patch(`/events/${eventId}`, {
        photos: nextPhotos,
        photo_url: nextPhotos[0] || null,
      });
      setEvent((e) => ({ ...e, photos: nextPhotos, photo_url: nextPhotos[0] || null }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update photos');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream/30">
        <Loader2 size={32} className="animate-spin text-teal" />
      </div>
    );
  }
  if (!event) return null;

  const photos = event.photos || (event.photo_url ? [event.photo_url] : []);
  const displayVariant = photos.length >= 2 ? 'gallery' : 'hero';
  const capacityText = event.max_capacity ? `${event.rsvp_count || 0} / ${event.max_capacity}` : `${event.rsvp_count || 0}`;
  const isFull = event.max_capacity && event.rsvp_count >= event.max_capacity;
  const isPastEvent = event.event_date && new Date(event.event_date) < new Date();

  return (
    <div className="min-h-screen bg-cream/30" data-testid="event-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-border-subtle sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          {isAuthenticated ? (
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-mid hover:text-navy">
              <ArrowLeft size={18} /> Back
            </button>
          ) : (
            <Link to="/" className="font-display text-xl text-navy">
              Cali<span className="italic text-teal">Moms</span> Connect
            </Link>
          )}
          <div className="flex items-center gap-2">
            <Button
              onClick={handleShare}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="event-share-btn"
            >
              {copiedShare ? <Check size={14} className="mr-1 text-green-600" /> : <Share2 size={14} className="mr-1" />}
              {copiedShare ? 'Copied!' : 'Share'}
            </Button>
            {isOwner && (
              <>
                <Button
                  onClick={() => setPhotosOpen(true)}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  data-testid="event-manage-photos-btn"
                >
                  <ImageIcon size={14} className="mr-1" /> Photos
                </Button>
                <Button
                  onClick={() => navigate(user.is_admin ? '/admin/dashboard?tab=events' : '/coadmin/dashboard')}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  data-testid="event-edit-btn"
                >
                  <Edit3 size={14} className="mr-1" /> Edit
                </Button>
                <Button
                  onClick={handleDelete}
                  variant="outline"
                  size="sm"
                  className="rounded-full text-red-600 border-red-300 hover:bg-red-50"
                  data-testid="event-delete-btn"
                >
                  <Trash2 size={14} />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-6">
        {/* Hero / Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl overflow-hidden shadow-card bg-white mb-6"
          data-testid="event-hero-container"
        >
          {photos.length >= 2 ? (
            <div className="p-3">
              <EventPhotoDisplay event={event} variant="gallery" testIdPrefix="event-detail-photo" />
            </div>
          ) : (
            <EventPhotoDisplay event={event} variant={photos.length === 1 ? 'hero' : 'hero'} testIdPrefix="event-detail-photo" />
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-3xl shadow-card p-6 md:p-8 mb-6"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h1 className="font-display text-3xl md:text-4xl text-navy mb-2" data-testid="event-title">
                {event.title}
              </h1>
              {event.host_first_name && (
                <p className="text-sm text-text-mid">Hosted by {event.host_first_name}</p>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {event.is_paid ? (
                (() => {
                  const effCents = typeof event.effective_price_cents === 'number'
                    ? event.effective_price_cents
                    : Math.round(Number(event.price || 0) * 100);
                  const isMemberPrice = event.effective_pricing_label === 'member';
                  return (
                    <>
                      <Badge className="bg-navy text-white" data-testid="event-price-badge">
                        <DollarSign size={12} className="mr-1" />
                        {(effCents / 100).toFixed(2)}
                      </Badge>
                      {isMemberPrice && (
                        <Badge className="bg-teal/15 text-teal-deep border border-teal/30" data-testid="event-member-price-badge">
                          Member Price
                        </Badge>
                      )}
                      {!isMemberPrice && event.member_discount_price != null && event.member_discount_price >= 0 && event.member_discount_price < Number(event.price) && (
                        <Badge variant="outline" className="text-teal-deep border-teal/40" data-testid="event-member-price-teaser">
                          Members save — ${Number(event.member_discount_price).toFixed(2)}
                        </Badge>
                      )}
                    </>
                  );
                })()
              ) : (
                <Badge className="bg-teal/10 text-teal-deep border-teal/30" variant="outline">Free event</Badge>
              )}
              {isFull && <Badge className="bg-orange-100 text-orange-700">Full · join waitlist</Badge>}
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 border-t border-b border-border-subtle py-4 mb-6">
            <div className="flex items-start gap-3">
              <Calendar size={18} className="text-teal flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-wide text-text-mid">When</p>
                <p className="text-sm text-navy font-medium">{formatEventDate(event.event_date)}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin size={18} className="text-teal flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-wide text-text-mid">Where</p>
                <p className="text-sm text-navy font-medium">
                  {event.location ? `${event.location}, ` : ''}{event.city}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users size={18} className="text-teal flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-wide text-text-mid">Attending</p>
                <p className="text-sm text-navy font-medium">{capacityText}</p>
              </div>
            </div>
          </div>

          {event.description && (
            <div className="mb-6">
              <h3 className="font-display text-xl text-navy mb-2">About this event</h3>
              <p className="text-text-mid leading-relaxed whitespace-pre-wrap">{event.description}</p>
            </div>
          )}

          {/* Workshop conversion surface (Phase 2) */}
          {isAuthenticated && !isPastEvent && (event.event_type || '').toLowerCase() === 'workshop' && event.workshop && !event.user_rsvped && !event.user_has_paid && (
            <>
              {event.workshop.pricing_label === 'workshop_full' && event.workshop.base_price > 0 && (
                <WorkshopCheckoutUpgradeCard
                  event={event}
                  basePriceCents={Math.round(event.workshop.base_price * 100)}
                  onPayFullPrice={handleCheckout}
                  fullPriceLoading={checkoutLoading}
                />
              )}
              {event.workshop.pricing_label === 'workshop_member_30off' && (
                <WorkshopMemberDiscountBanner
                  basePriceCents={Math.round(event.workshop.base_price * 100)}
                  nextResetIso={event.workshop.next_reset_iso}
                />
              )}
            </>
          )}

          {/* Workshop host + what to bring */}
          {(event.event_type || '').toLowerCase() === 'workshop' && (event.host_name || event.what_to_bring) && (
            <div className="mb-6 bg-cream/60 border border-border-subtle rounded-2xl p-5 space-y-2" data-testid="workshop-info-block">
              {event.host_name && (
                <p className="text-sm text-navy"><span className="font-semibold">Hosted by:</span> {event.host_name}</p>
              )}
              {event.host_bio && <p className="text-xs text-text-mid">{event.host_bio}</p>}
              {event.what_to_bring && (
                <p className="text-sm text-navy"><span className="font-semibold">What to bring:</span> {event.what_to_bring}</p>
              )}
            </div>
          )}

          {/* Guest pass panel (annual subscribers, workshops only, upcoming events) */}
          {isAuthenticated && !isPastEvent && (event.event_type || '').toLowerCase() === 'workshop' && guestPass?.status === 'available' && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="guest-pass-panel">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bringGuest}
                  onChange={(e) => setBringGuest(e.target.checked)}
                  className="w-4 h-4 mt-1 accent-teal"
                  data-testid="bring-guest-toggle"
                />
                <div>
                  <p className="font-semibold text-navy">Bring a friend free — you have 1 guest pass available</p>
                    <p className="text-xs text-text-mid mt-0.5">
                    Your annual-member guest pass reserves them a free seat. They'll get an email invitation and
                    create a free account to claim it (72-hour window).
                  </p>
                </div>
              </label>
              {bringGuest && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Guest's first name"
                    className="rounded-xl border border-border-subtle px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal/40"
                    data-testid="guest-first-name-input"
                  />
                  <input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="Guest's email"
                    className="rounded-xl border border-border-subtle px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal/40"
                    data-testid="guest-email-input"
                  />
                  {(event.user_rsvped || event.user_has_paid) && (
                    <Button
                      onClick={async () => {
                        if (!guestName.trim() || !guestEmail.trim()) { toast.error('Enter your guest\'s name and email'); return; }
                        setInvitingGuest(true);
                        try {
                          const res = await api.post('/guest-pass/invite', {
                            event_id: eventId, guest_first_name: guestName.trim(), guest_email: guestEmail.trim(),
                          });
                          toast.success(res.data.message);
                          setBringGuest(false);
                          const gp = await api.get('/guest-pass/me');
                          setGuestPass(gp.data);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Could not send invitation');
                        }
                        setInvitingGuest(false);
                      }}
                      disabled={invitingGuest}
                      className="sm:col-span-2 bg-teal hover:bg-teal-deep text-white rounded-full"
                      data-testid="send-guest-invite-btn"
                    >
                      {invitingGuest ? <Loader2 size={16} className="animate-spin mr-2" /> : <UserPlus size={16} className="mr-2" />}
                      Send guest invitation
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
          {isAuthenticated && (event.event_type || '').toLowerCase() === 'workshop' && guestPass?.status === 'pending' && guestPass?.event_id === eventId && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-navy" data-testid="guest-pass-pending-note">
              <p><strong>{guestPass.guest_first_name}</strong> has been invited as your guest — their seat is reserved while they claim it.</p>
            </div>
          )}

          {/* RSVP Action */}
          {isAuthenticated ? (
            <div className="flex flex-col sm:flex-row gap-3" data-testid="event-rsvp-actions">
              {(() => {
                if (isPastEvent) return null;
                const isWorkshop = (event.event_type || '').toLowerCase() === 'workshop';
                if (isWorkshop && !event.user_rsvped && !event.user_has_paid && event.workshop) {
                  const label = event.workshop.pricing_label;
                  const cents = event.workshop.effective_price_cents;
                  if (label === 'workshop_free_credit') {
                    return (
                      <Button
                        onClick={handleCheckout}
                        disabled={checkoutLoading}
                        className="bg-teal hover:bg-teal-deep text-white rounded-full px-8 py-6 text-base"
                        data-testid="workshop-register-credit-btn"
                      >
                        {checkoutLoading ? <Loader2 size={18} className="animate-spin mr-2" /> : <Ticket size={18} className="mr-2" />}
                        Register with monthly credit — free
                      </Button>
                    );
                  }
                  if (label === 'workshop_member_30off') {
                    return (
                      <Button
                        onClick={handleCheckout}
                        disabled={checkoutLoading}
                        className="bg-navy hover:bg-navy/90 text-white rounded-full px-8 py-6 text-base"
                        data-testid="workshop-register-discount-btn"
                      >
                        {checkoutLoading ? <Loader2 size={18} className="animate-spin mr-2" /> : <Ticket size={18} className="mr-2" />}
                        Register — ${(cents / 100).toFixed(2)} (member price)
                      </Button>
                    );
                  }
                  // workshop_full → button is rendered inside the upgrade card via onPayFullPrice
                  return null;
                }

                if (event.is_paid && !event.user_has_paid && !event.user_rsvped) {
                  const effCents = typeof event.effective_price_cents === 'number'
                    ? event.effective_price_cents
                    : Math.round(Number(event.price || 0) * 100);
                  return (
                    <Button
                      onClick={handleCheckout}
                      disabled={checkoutLoading}
                      className="bg-navy hover:bg-navy/90 text-white rounded-full px-8 py-6 text-base"
                      data-testid="event-buy-ticket-btn"
                    >
                      {checkoutLoading ? <Loader2 size={18} className="animate-spin mr-2" /> : <Ticket size={18} className="mr-2" />}
                      Buy Ticket — ${(effCents / 100).toFixed(2)}
                      {event.effective_pricing_label === 'member' && <span className="ml-2 text-xs opacity-80">(member price)</span>}
                    </Button>
                  );
                }

                return (
                  <Button
                    onClick={handleRSVP}
                    disabled={rsvping}
                    className={`rounded-full px-8 py-6 text-base ${event.user_rsvped ? 'bg-white border-2 border-teal text-teal hover:bg-teal/5' : 'bg-teal hover:bg-teal-deep text-white'}`}
                    data-testid="event-rsvp-btn"
                  >
                    {rsvping && <Loader2 size={18} className="animate-spin mr-2" />}
                    {event.user_rsvped ? 'Cancel RSVP' : (isFull ? 'Join Waitlist' : 'RSVP')}
                  </Button>
                );
              })()}
            </div>
          ) : (
            <div className="bg-gradient-to-br from-teal/10 to-seafoam/30 border border-teal/30 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="event-signup-cta">
              <div>
                <p className="font-display text-xl text-navy">Join CaliMoms Connect to RSVP</p>
                <p className="text-sm text-text-mid mt-1">Create your free account to save your spot at this event and connect with moms in {event.city}.</p>
              </div>
              <Button
                onClick={() => navigate(`/signup?redirect=/events/${eventId}`)}
                className="bg-teal hover:bg-teal-deep text-white rounded-full px-6 py-5 text-base whitespace-nowrap"
                data-testid="event-signup-btn"
              >
                <UserPlus size={16} className="mr-2" /> Sign Up & RSVP
              </Button>
            </div>
          )}

          {event.event_date && new Date(event.event_date) < new Date() && (
            <div className="mt-4 flex items-center gap-2 text-sm text-text-mid">
              <Clock size={14} /> This event has already taken place.
            </div>
          )}

          {/* Check-in QR for registered members (before the event) */}
          {isAuthenticated && myTicket && event.event_date && new Date(event.event_date) > new Date() && (
            <div className="mt-6 bg-cream/60 border border-dashed border-teal/40 rounded-2xl p-5 flex items-center gap-4" data-testid="my-checkin-qr">
              <QRCodeCanvas value={myTicket.ticket_code} size={88} fgColor="#0d3b66" bgColor="transparent" level="M" />
              <div>
                <p className="font-semibold text-navy text-sm">Your check-in code</p>
                <p className="text-xs text-text-mid mt-0.5">Show this QR at the door — the host scans it to check you in.</p>
                <p className="text-xs font-mono text-teal mt-1" data-testid="my-ticket-code">{myTicket.ticket_code}</p>
              </div>
            </div>
          )}

          {/* Attendee photo wall (opens once the event starts) */}
          {isAuthenticated && <EventPhotoWall eventId={eventId} />}
        </motion.div>
      </main>

      {/* Photos management dialog (owner only) */}
      {isOwner && (
        <Dialog open={photosOpen} onOpenChange={setPhotosOpen}>
          <DialogContent className="max-w-2xl" data-testid="event-photos-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-navy">Event Photos — {event.title}</DialogTitle>
              <DialogDescription>Drag and drop or click to add photos. Up to 4 per event.</DialogDescription>
            </DialogHeader>
            <EventPhotoUploader
              value={photos}
              onChange={handlePhotosChange}
              testIdPrefix="event-detail-photos"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
